import React from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Card, Button } from "@/components/ui";

function renderValue(v) {
  if (Array.isArray(v)) {
    if (!v.length || typeof v[0] !== "object") return <span className="text-muted-foreground">{v.join(", ") || "—"}</span>;
    const cols = Object.keys(v[0]);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-xs text-muted-foreground">{cols.map((c) => <th key={c} className="px-3 py-1.5 text-start capitalize">{c.replace(/_/g, " ")}</th>)}</tr></thead>
          <tbody>{v.map((r, i) => <tr key={i} className="border-b border-border/40 last:border-0">{cols.map((c) => <td key={c} className="px-3 py-1.5">{String(r[c])}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  if (v && typeof v === "object") return <span className="mono text-xs text-muted-foreground">{JSON.stringify(v)}</span>;
  return <span className="font-bold">{String(v)}</span>;
}

export default function Materials() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();

  const { data, loading, error } = useDeliverable(
    () => api.editorMaterials({ model: wm.model, floors: wm.floors || 1, wall_height_m: 3.2, slab_system: wm.slab_system, design_code: wm.design_code }),
    [wm?.title, wm?.design_code],
    !!wm?.model
  );

  const entries = data ? Object.entries(data).filter(([, v]) => v != null) : [];

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "الكميات والمواد" : "Materials / BOQ"}
            title={isAr ? "حصر المواد وجدول الكميات" : "Material take-off & BOQ"}
            right={<Button size="sm" variant="outline" onClick={() => api.editorDownloadExport("boq", wm.model, wm.title).catch((e) => toast.error(String(e.message || e)))} data-testid="materials-boq"><FileSpreadsheet size={15} /> {isAr ? "تصدير BOQ" : "Export BOQ"}</Button>}
          />
          <DeliverableState loading={loading} error={error} isAr={isAr}>
            <div className="space-y-3">
              {entries.map(([k, v]) => (
                <Card key={k}>
                  <p className="mb-2 text-sm font-black capitalize">{k.replace(/_/g, " ")}</p>
                  {renderValue(v)}
                </Card>
              ))}
            </div>
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
